import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import ApiURL from '../Api/Api';
import TransactionProofImage from './TransactionProofImage';
import {
  FunnelIcon,
  ArrowPathIcon,
  BanknotesIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  TableCellsIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v))
    : '₹0';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ── stat card ───────────────────────────────────────────────── */
const StatCard = ({ label, value, icon: Icon, bg, iconBg, textColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.3 }}
    className={`${bg} rounded-2xl p-3.5 sm:p-4 flex items-center gap-3`}
  >
    <div className={`${iconBg} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
      <Icon className="w-4.5 h-4.5 text-white w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className={`text-sm sm:text-base font-bold ${textColor} leading-tight`}>{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider font-semibold">{label}</p>
    </div>
  </motion.div>
);

/* ── status badge ────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  const map = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    inprogress: 'bg-blue-50 text-blue-700 border-blue-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
  };
  const icons = {
    pending: <ClockIcon className="w-2.5 h-2.5 mr-1" />,
    inprogress: <ArrowPathIcon className="w-2.5 h-2.5 mr-1" />,
    approved: <CheckCircleIcon className="w-2.5 h-2.5 mr-1" />,
    rejected: <XCircleIcon className="w-2.5 h-2.5 mr-1" />,
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full border ${map[s] || map.pending}`}>
      {icons[s] || icons.pending}
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
};

/* ── Mobile Card Component ───────────────────────────────────── */
const RequestCard = ({ request, index, role, selectedStatus, selectedRequestId, rejectReason, onStatusChange, onRejectReasonChange, onAction }) => {
  const showActions = (request.status === 'inprogress') && 
                      request.approval_role === 'approver' && 
                      ['admin', 'company_owner'].includes(role);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      className={`bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform duration-75 ${
        request.status === 'approved' ? 'border-l-4 border-l-emerald-400' :
        request.status === 'rejected' ? 'border-l-4 border-l-red-400' :
        'border-l-4 border-l-amber-400'
      }`}
    >
      <div className="px-4 py-3.5">
        {/* Header row: ID + status + amount */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
              #{request.id}
            </span>
            <StatusBadge status={request.status} />
          </div>
          <p className={`text-base font-bold ${
            request.status === 'approved' ? 'text-emerald-600' :
            request.status === 'rejected' ? 'text-red-600' :
            'text-amber-600'
          }`}>
            {fmt(request.amount)}
          </p>
        </div>

        {/* Requester */}
        <div className="mb-2">
          <div className="flex items-center gap-1.5">
            <UserGroupIcon className="w-3.5 h-3.5 text-slate-400" />
            <p className="text-[15px] font-bold text-slate-800">{request.user_name || '—'}</p>
          </div>
        </div>

        {/* Purpose */}
        {request.purpose && (
          <div className="mb-2 flex items-start gap-1.5">
            <DocumentTextIcon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-slate-600 leading-relaxed">{request.purpose}</p>
          </div>
        )}

        {/* Created & Updated info */}
        <div className="mt-2 pt-2 border-t border-slate-50">
          <div className="flex flex-col gap-1 text-[9px] text-slate-400">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-3 h-3" />
              <span>Created: {fmtDateTime(request.created_at)}</span>
              {request.created_by && <span>by {request.created_by}</span>}
            </div>
            {request.updated_at && request.updated_at !== request.created_at && (
              <div className="flex items-center gap-2">
                <ArrowPathIcon className="w-3 h-3" />
                <span>Updated: {fmtDateTime(request.updated_at)}</span>
                {request.updater_name && <span>by {request.updater_name}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Rejection reason if any */}
        {request.rejection_reason && (
          <div className="mt-2 text-[10px] text-red-400 italic truncate">
            Reason: {request.rejection_reason}
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="mt-3 pt-2 border-t border-slate-100">
            {!selectedStatus[request.id] ? (
              <div className="flex gap-2">
                <button
                  onClick={() => onStatusChange(request.id, 'approve')}
                  className="flex-1 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  Approve
                </button>
                <button
                  onClick={() => onStatusChange(request.id, 'reject')}
                  className="flex-1 px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold rounded-xl hover:bg-red-100 transition-colors"
                >
                  Reject
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedStatus[request.id] === 'reject' && (
                  <input
                    type="text"
                    value={rejectReason[request.id] || ''}
                    onChange={(e) => onRejectReasonChange(request.id, e.target.value)}
                    placeholder="Reason for rejection..."
                    className="w-full p-2 border border-red-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-red-300 bg-red-50/50"
                  />
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => onAction(request.id)}
                    className="flex-1 px-3 py-1.5 bg-indigo-600 text-white text-[11px] font-bold rounded-xl hover:bg-indigo-700 transition-colors"
                  >
                    Submit
                  </button>
                  <button
                    onClick={() => onStatusChange(request.id, null)}
                    className="px-3 py-1.5 bg-slate-100 text-slate-600 text-[11px] font-bold rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
const WorkerRequestReport = () => {
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const userRole = localStorage.getItem('role') || 'worker';
  const userId = localStorage.getItem('id');
  const highlightedRequestId = location.state?.highlightedRequestId;

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'cards';
    return 'table';
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedStatus, setSelectedStatus] = useState({});
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [rejectReason, setRejectReason] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const requestRefs = useRef({});

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!userId) {
      setError('User not authenticated');
      toast.error('User not authenticated');
      navigate('/login');
      return;
    }
    fetchRequests();
  }, []);

  useEffect(() => {
    if (highlightedRequestId && requestRefs.current[highlightedRequestId]) {
      requestRefs.current[highlightedRequestId].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [highlightedRequestId, isLoading]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${ApiURL}/getWorkerRequests.php`, {
        params: { user_id: userId, role: userRole },
      });
      setRequests(response.data || []);
      setError('');
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setError(`Network error: ${errorMessage}`);
      toast.error(`Error fetching requests: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (requestId, status) => {
    if (status === null) {
      setSelectedStatus((prev) => {
        const newState = { ...prev };
        delete newState[requestId];
        return newState;
      });
      setSelectedRequestId(null);
    } else {
      setSelectedStatus((prev) => ({ ...prev, [requestId]: status }));
      setSelectedRequestId(requestId);
    }
  };

  const handleRejectReasonChange = (requestId, reason) => {
    setRejectReason((prev) => ({ ...prev, [requestId]: reason }));
  };

  const handleAction = async (requestId) => {
    const action = selectedStatus[requestId];
    const reason = rejectReason[requestId] || '';
    const actorId = localStorage.getItem('id');

    if (!action) {
      toast.error('Please select an action');
      return;
    }
    if (!actorId) {
      toast.error('User not authenticated');
      return;
    }
    if (action === 'reject' && !reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    if (action === 'approve') {
      const request = requests.find((r) => r.id === requestId);
      const confirmApproval = window.confirm(
        `Are you sure you want to approve this request? A transaction of ${fmt(request?.amount || 0)} will be created for ${request?.user_name || 'the worker'}.`
      );
      if (!confirmApproval) {
        toast.info('Approval cancelled');
        return;
      }
    }

    const payload = {
      request_id: Number(requestId),
      action: action,
      reason: action === 'reject' ? reason : '',
      admin_id: Number(actorId),
    };

    try {
      const response = await axios.post(`${ApiURL}/updateWorkerRequest.php`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.status === 'success') {
        toast.success('Request updated successfully');
        fetchRequests();
        setRejectReason((prev) => ({ ...prev, [requestId]: '' }));
        setSelectedRequestId(null);
        setSelectedStatus((prev) => {
          const newState = { ...prev };
          delete newState[requestId];
          return newState;
        });
      } else {
        toast.error(response.data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      toast.error(`Error updating request: ${errorMessage}`);
    }
  };

  const stats = useMemo(() => {
    const totalRequests = requests.length;
    const totalApproved = requests.filter(r => r.status === 'approved').length;
    const totalRejected = requests.filter(r => r.status === 'rejected').length;
    const totalPending = requests.filter(r => r.status === 'pending' || r.status === 'inprogress').length;
    const totalAmount = requests
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);
    
    return { totalRequests, totalApproved, totalRejected, totalPending, totalAmount };
  }, [requests]);

  const filtered = useMemo(() => {
    let list = [...requests];
    
    if (statusFilter !== 'all') {
      list = list.filter(r => r.status === statusFilter);
    }
    
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.user_name || '').toLowerCase().includes(q) ||
        (r.purpose || '').toLowerCase().includes(q) ||
        (r.id?.toString() || '').includes(q)
      );
    }
    
    if (dateRange.startDate) {
      list = list.filter(r => r.created_at && new Date(r.created_at) >= new Date(dateRange.startDate));
    }
    if (dateRange.endDate) {
      list = list.filter(r => r.created_at && new Date(r.created_at) <= new Date(dateRange.endDate));
    }
    
    return list;
  }, [requests, statusFilter, search, dateRange]);

  const exportCSV = () => {
    if (!filtered.length) return;
    
    const headers = ['#', 'Request ID', 'Worker Name', 'Purpose', 'Amount', 'Status', 'Created At', 'Updated At', 'Updated By', 'Rejection Reason'];
    const rows = filtered.map((r, i) => [
      i + 1,
      r.id || '',
      r.user_name || '',
      r.purpose || '',
      r.amount || 0,
      r.status || '',
      fmtDateTime(r.created_at),
      r.updated_at ? fmtDateTime(r.updated_at) : '',
      r.updater_name || '',
      r.rejection_reason || '',
    ]);
    
    const csv = [headers, ...rows].map(row => 
      row.map(cell => {
        const v = String(cell ?? '');
        return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(',')
    ).join('\n');
    
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = 'fund_requests_report.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const hasActiveFilter = statusFilter !== 'all' || search || dateRange.startDate || dateRange.endDate;
  const showCardsView = viewMode === 'cards';
  const canTakeAction = ['admin', 'company_owner'].includes(userRole);

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6">

            {/* ── Page header ── */}
            <div className="flex items-center justify-between mb-3 sm:mb-5 gap-2">
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">
                  Fund Request Report
                </h1>
                <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">
                  {filtered.length} of {requests.length} requests
                </p>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* View toggle */}
                <div className="flex items-center bg-white border border-slate-200 rounded-lg sm:rounded-xl p-0.5 sm:p-1 shadow-sm">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center transition-all touch-manipulation ${
                      viewMode === 'table' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    aria-label="Table view"
                  >
                    <TableCellsIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center transition-all touch-manipulation ${
                      viewMode === 'cards' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    aria-label="Card view"
                  >
                    <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>

                {/* Filter */}
                <button
                  onClick={() => setFilterOpen(p => !p)}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl border text-xs font-semibold transition-all shadow-sm touch-manipulation ${
                    hasActiveFilter ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <FunnelIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Filter</span>
                  {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-white/80" />}
                </button>

                {/* Export */}
                <button
                  onClick={exportCSV}
                  disabled={!filtered.length}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                >
                  <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>

                {/* Refresh */}
                <button
                  onClick={fetchRequests}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-indigo-600 transition-all shadow-sm touch-manipulation"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Filter panel */}
            <AnimatePresence>
              {filterOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Filters</p>
                      {hasActiveFilter && (
                        <button 
                          onClick={() => { setStatusFilter('all'); setSearch(''); setDateRange({ startDate: '', endDate: '' }); }}
                          className="text-[11px] text-rose-500 font-semibold hover:text-rose-600 touch-manipulation"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                        {[
                          { label: 'From', key: 'startDate' },
                          { label: 'To', key: 'endDate' },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">{label}</label>
                            <div className="relative">
                              <input 
                                type="date" 
                                value={dateRange[key]}
                                onChange={e => setDateRange(p => ({ ...p, [key]: e.target.value }))}
                                className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" 
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Status</label>
                        <div className="flex gap-1 h-9 bg-slate-50 border border-slate-200 rounded-xl p-1">
                          {['all', 'pending', 'approved', 'rejected'].map(v => (
                            <button 
                              key={v} 
                              onClick={() => setStatusFilter(v)}
                              className={`flex-1 text-[11px] font-bold rounded-lg capitalize transition-all touch-manipulation ${
                                statusFilter === v
                                  ? v === 'approved' ? 'bg-emerald-500 text-white shadow-sm'
                                  : v === 'rejected' ? 'bg-red-500 text-white shadow-sm'
                                  : v === 'pending' ? 'bg-amber-500 text-white shadow-sm'
                                  : 'bg-indigo-600 text-white shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Search</label>
                      <div className="relative">
                        <input 
                          type="text" 
                          value={search} 
                          onChange={e => setSearch(e.target.value)}
                          placeholder="Search by requester name, purpose, or request ID..."
                          className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" 
                        />
                        {search && (
                          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 touch-manipulation">
                            <XMarkIcon className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-4">
              <StatCard 
                label="Total Requests" 
                value={stats.totalRequests} 
                icon={DocumentTextIcon}
                bg="bg-slate-50" 
                iconBg="bg-slate-500" 
                textColor="text-slate-700" 
                delay={0} 
              />
              <StatCard 
                label="Approved" 
                value={stats.totalApproved} 
                icon={CheckCircleIcon}
                bg="bg-emerald-50" 
                iconBg="bg-emerald-500" 
                textColor="text-emerald-700" 
                delay={0.05} 
              />
              <StatCard 
                label="Rejected" 
                value={stats.totalRejected} 
                icon={XCircleIcon}
                bg="bg-red-50" 
                iconBg="bg-red-500" 
                textColor="text-red-700" 
                delay={0.1} 
              />
              <StatCard 
                label="Pending" 
                value={stats.totalPending} 
                icon={ClockIcon}
                bg="bg-amber-50" 
                iconBg="bg-amber-500" 
                textColor="text-amber-700" 
                delay={0.15} 
              />
              <StatCard 
                label="Total Approved Amt" 
                value={fmt(stats.totalAmount)} 
                icon={CurrencyRupeeIcon}
                bg="bg-indigo-50" 
                iconBg="bg-indigo-500" 
                textColor="text-indigo-700" 
                delay={0.2} 
              />
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">{error}</div>
            )}

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl h-20 animate-pulse border border-slate-100" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 py-16 flex flex-col items-center gap-3 shadow-sm">
                <BanknotesIcon className="w-10 h-10 text-slate-200" />
                <p className="text-sm font-semibold text-slate-400">No fund requests found</p>
                {hasActiveFilter && (
                  <button
                    onClick={() => { setStatusFilter('all'); setSearch(''); setDateRange({ startDate: '', endDate: '' }); }}
                    className="text-xs text-indigo-500 font-semibold touch-manipulation"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* CARD VIEW */}
                {showCardsView && (
                  <div className="space-y-2.5">
                    {filtered.map((request, idx) => (
                      <RequestCard 
                        key={`card-${request.id}`}
                        request={request}
                        index={idx}
                        role={userRole}
                        selectedStatus={selectedStatus}
                        selectedRequestId={selectedRequestId}
                        rejectReason={rejectReason}
                        onStatusChange={handleStatusChange}
                        onRejectReasonChange={handleRejectReasonChange}
                        onAction={handleAction}
                      />
                    ))}
                    <div className="flex justify-center pt-3 pb-2">
                      <button 
                        onClick={exportCSV} 
                        disabled={!filtered.length}
                        className="flex items-center gap-2 px-5 py-2.5 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-xl active:bg-indigo-100 transition-colors touch-manipulation"
                      >
                        <DocumentArrowDownIcon className="w-4 h-4" />
                        Export CSV ({filtered.length} requests)
                      </button>
                    </div>
                  </div>
                )}

                {/* TABLE VIEW */}
                {!showCardsView && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px] text-left border-collapse min-w-[1100px]">
                        <thead>
                          <tr className="border-b border-slate-100 bg-slate-50/80">
                            {[
                              '#', 'ID', 'Requester', 'Purpose', 'Amount', 
                              'Status', 'Proof', 'Created At', 'Updated At', 'Updated By', 'Rejection Reason',
                              ...(canTakeAction ? ['Actions'] : []),
                            ].map(h => (
                              <th key={h} className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {filtered.map((request, i) => {
                            const showActions = (request.status === 'inprogress') && 
                                                request.approval_role === 'approver' && 
                                                canTakeAction;
                            const isHighlighted = request.id === highlightedRequestId;
                            
                            return (
                              <motion.tr 
                                key={request.id}
                                ref={(el) => {
                                  if (el && isHighlighted) requestRefs.current[request.id] = el;
                                }}
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: 1 }}
                                transition={{ delay: Math.min(i * 0.02, 0.2) }}
                                className={`hover:bg-slate-50/60 transition-colors group ${
                                  isHighlighted ? 'bg-amber-50' : ''
                                }`}
                              >
                                <td className="px-3 py-2.5 text-slate-400 font-medium">{i + 1}</td>
                                <td className="px-3 py-2.5 text-indigo-600 font-bold">#{request.id}</td>
                                <td className="px-3 py-2.5 text-slate-700 font-medium">{request.user_name || '—'}</td>
                                <td className="px-3 py-2.5 text-slate-600 max-w-[200px] truncate">{request.purpose || '—'}</td>
                                <td className="px-3 py-2.5 font-bold text-slate-700 whitespace-nowrap">{fmt(request.amount)}</td>
                                <td className="px-3 py-2.5"><StatusBadge status={request.status} /></td>
                                <td className="px-3 py-2.5"><TransactionProofImage path={request.attachment_path} /></td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap text-[10px]">{fmtDateTime(request.created_at)}</td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap text-[10px]">
                                  {request.updated_at && request.updated_at !== request.created_at ? fmtDateTime(request.updated_at) : '—'}
                                </td>
                                <td className="px-3 py-2.5 text-slate-500">{request.updater_name || '—'}</td>
                                <td className="px-3 py-2.5 text-red-400 text-[11px] max-w-[150px] truncate">{request.rejection_reason || '—'}</td>
                                {canTakeAction && (
                                  <td className="px-3 py-2.5">
                                    {showActions ? (
                                      <div className="flex items-center gap-2">
                                        {!selectedStatus[request.id] ? (
                                          <>
                                            <button
                                              onClick={() => handleStatusChange(request.id, 'approve')}
                                              className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold rounded-lg hover:bg-emerald-100 transition-colors whitespace-nowrap"
                                            >
                                              Approve
                                            </button>
                                            <button
                                              onClick={() => handleStatusChange(request.id, 'reject')}
                                              className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                                            >
                                              Reject
                                            </button>
                                          </>
                                        ) : (
                                          <div className="flex items-center gap-2">
                                            {selectedStatus[request.id] === 'reject' && selectedRequestId === request.id && (
                                              <input
                                                type="text"
                                                value={rejectReason[request.id] || ''}
                                                onChange={(e) => handleRejectReasonChange(request.id, e.target.value)}
                                                placeholder="Reason..."
                                                className="w-32 p-1 border border-red-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-300"
                                              />
                                            )}
                                            <button
                                              onClick={() => handleAction(request.id)}
                                              className="px-3 py-1 bg-indigo-600 text-white text-[10px] font-bold rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                                            >
                                              Submit
                                            </button>
                                            <button
                                              onClick={() => handleStatusChange(request.id, null)}
                                              className="px-3 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-[10px] text-slate-400 italic">
                                        {request.status === 'approved' ? '✓ Approved' : 
                                         request.status === 'rejected' ? '✗ Rejected' : 
                                         'Pending'}
                                      </span>
                                    )}
                                  </td>
                                )}
                              </motion.tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
                      <p className="text-[11px] text-slate-400">
                        Showing <span className="font-bold text-slate-600">{filtered.length}</span> requests
                      </p>
                      <button 
                        onClick={exportCSV} 
                        disabled={!filtered.length}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                      >
                        <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default WorkerRequestReport;
